import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const LookBook = ({ images = [] }) => {
  const [active, setActive] = useState(null); // index in `images` or null
  const touchStartX = useRef(null);

  const open = images.length;
  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i === null ? i : (i - 1 + images.length) % images.length)), [images.length]);
  const next = useCallback(() => setActive((i) => (i === null ? i : (i + 1) % images.length)), [images.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [active, close, prev, next]);

  if (!open) return null;
  const loop = [...images, ...images];

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next() : prev());
    touchStartX.current = null;
  };

  return (
    <section className="bg-[#0b0b0b] py-16 sm:py-20 overflow-hidden" data-testid="lookbook-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 text-center mb-10">
        <p className="overline text-gold-gradient mb-3">Styled by You</p>
        <h2 className="font-display text-3xl sm:text-5xl text-[#f7f3ec]">The Look Book</h2>
        <div className="gold-line w-20 mx-auto mt-5" />
        <p className="font-body text-xs text-[#9c9587] mt-4">Tap any photo to view full screen</p>
      </div>
      <div className="tmarquee-wrap group">
        <div className="tmarquee flex gap-4 w-max group-hover:[animation-play-state:paused]">
          {loop.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i % images.length)}
              data-testid={`lookbook-thumb-${i % images.length}`}
              className="w-[240px] sm:w-[300px] aspect-[3/4] shrink-0 overflow-hidden bg-[#141311] cursor-pointer group/thumb relative"
            >
              <img src={src} alt="Look book" className="w-full h-full object-cover img-zoom" />
              <div className="absolute inset-0 bg-[#0b0b0b]/0 group-hover/thumb:bg-[#0b0b0b]/20 transition-colors" />
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            className="fixed inset-0 z-[90] bg-[#0b0b0b]/95 backdrop-blur-sm flex items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            data-testid="lookbook-lightbox"
            onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
          >
            <button onClick={close} data-testid="lookbook-close"
              className="absolute top-5 right-5 z-10 text-[#e7d3a1] hover:text-white p-2">
              <X size={30} />
            </button>

            {images.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lookbook-prev"
                  className="absolute left-3 sm:left-8 z-10 text-[#e7d3a1] hover:text-white p-2 bg-[#0b0b0b]/40 rounded-full">
                  <ChevronLeft size={34} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lookbook-next"
                  className="absolute right-3 sm:right-8 z-10 text-[#e7d3a1] hover:text-white p-2 bg-[#0b0b0b]/40 rounded-full">
                  <ChevronRight size={34} />
                </button>
              </>
            )}

            <AnimatePresence mode="wait">
              <motion.img
                key={active}
                src={images[active]}
                alt="Look book full"
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.25 }}
                className="max-h-[86vh] max-w-[92vw] object-contain shadow-2xl select-none"
              />
            </AnimatePresence>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 overline text-[0.6rem] text-[#9c9587]">
              {active + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default LookBook;
